import { takeEvery, put } from 'redux-saga/effects'
import { startSubmit, stopSubmit } from 'redux-form/immutable'
import i18next from 'i18next'

import { formatByISO, ISO_CODE, USA_ISO } from 'app/constants/phoneCountryCodes'
import isEmail from 'helpers/isEmail'
import { INVITES_FORM_SUBMIT } from 'store/entities/organizations/actionTypes'
import { adminOrganizationUsers } from 'store/entities/organizations/actions'
import { ADMIN_ORGANIZATION_USERS_INVITE_FORM as FORM_NAME } from 'app/constants/adminOrganization'
import { parseInvites } from 'helpers/parseInvites'
import { parsePhoneBeforeSend, removePhoneCodeFromPhone } from 'helpers/parsePhone'
import { prepareArray } from 'helpers/common'

export default function* invitesFormSaga() {
  yield takeEvery(INVITES_FORM_SUBMIT, handleInvitesFormSubmit)
}

const isDublicate = (arg, ind, arr, type) => arr.some((item, i) => i > ind && item[type] === arg)

const emailDuplicated = email => i18next.t(`Email is duplicate`, { email })

const phoneDuplicated = phone => i18next.t(`Phone is duplicate`, { phone })

const phoneString = phone => i18next.t(`Invalid phone number is`, { phone })

const glueArray = (arr, fn, property) => arr.reduce((string, el) => `${string} ${fn(el[property])}`, '')

const filterByNonEmptyEmail = users => users.filter(user => user.email)

export const isInvalidPhoneNumber = (phone, iso = USA_ISO) => {
  const parsedPhone = parsePhoneBeforeSend(phone)

  const code = ISO_CODE[iso]
  const phoneWithoutCountryCode = removePhoneCodeFromPhone(parsedPhone, iso)
  const phoneWithoutCountryCodeLength = phoneWithoutCountryCode.length
  const phoneValidationFormat = formatByISO[iso]

  return (
    phoneWithoutCountryCodeLength < phoneValidationFormat.min ||
    phoneWithoutCountryCodeLength > phoneValidationFormat.max ||
    `${code}${phoneWithoutCountryCode}` !== parsedPhone
  )
}

export function* handleInvitesFormSubmit({ payload }) {
  try {
    const { values: immutableformData, isBulk } = payload
    const { users, bulkInvites, isSchedule, schedule } = immutableformData.toJS()
    let invites
    yield put(startSubmit(FORM_NAME))
    if (isBulk) {
      // for this method checking teams for existing empty name will provide on backend side so that the user interface does not load

      invites = filterByNonEmptyEmail(parseInvites(bulkInvites))

      const duplicateEmails = invites.filter((item, i, arr) => isDublicate(item.email, i, arr, 'email'))
      if (duplicateEmails.length) {
        return yield put(stopSubmit(FORM_NAME, { bulkInvites: glueArray(duplicateEmails, emailDuplicated, 'email') }))
      }

      const notIsEmails = invites.filter(({ email }) => !isEmail(email))
      if (notIsEmails.length) {
        const messageRightPart = notIsEmails.length === 1 ? ' is not an email' : ' are not emails'
        return yield put(
          stopSubmit(FORM_NAME, {
            bulkInvites: notIsEmails.map(el => el.email).join(', ') + i18next.t(messageRightPart),
          })
        )
      }

      const duplicatePhones = invites.filter((item, i, arr) => isDublicate(item.phone, i, arr, 'phone') && !!item.phone)
      if (duplicatePhones.length) {
        return yield put(stopSubmit(FORM_NAME, { bulkInvites: glueArray(duplicatePhones, phoneDuplicated, 'phone') }))
      }

      const invalidPhoneNumbers = invites.filter(item => item.phone && isInvalidPhoneNumber(item.phone, item.phoneISO))
      if (invalidPhoneNumbers.length) {
        return yield put(stopSubmit(FORM_NAME, { bulkInvites: glueArray(invalidPhoneNumbers, phoneString, 'phone') }))
      }
      prepareArray(invites, parsePhoneBeforeSend, 'phone')
    } else {
      invites = filterByNonEmptyEmail(users)
      if (!invites.length) {
        return yield put(stopSubmit(FORM_NAME))
      }
      prepareArray(invites, parsePhoneBeforeSend, 'phone')
    }
    yield put(adminOrganizationUsers.post.request({ invites, isSchedule, schedule }))
  } catch (error) {
    throw new Error(error)
  }
}
